import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import accountAnalytics from '~/analytics/account/accountAnalytics'
import { ResendEmailErrorCode } from '~/auth/types/auth.types'

import { ButtonClear } from '~/components/Buttons/ButtonClear'
import Spinner from '~/components/Spinner'
import IconWithMessage from '~/components/TopBanner/IconWithMessage'
import { useSendVerifyMailMutation } from '~/redux/apis/accounts-api'

export default function EmailVerification({ email }: { email?: string | null }): React.ReactElement {
  const { t } = useTranslation(['emailVerify', 'errors'])
  const [error, setError] = useState<string>('')
  const [sendEmailVerification, { isLoading, isSuccess, isError, error: apiError, isUninitialized }] =
    useSendVerifyMailMutation()

  useEffect(() => {
    if (isError) {
      switch (apiError?.data?.errorCode) {
        case ResendEmailErrorCode.EMAIL_IN_USER:
          setError(t('errors:emailInUse'))
          return
        default:
          setError(t(`emailVerify:${apiError?.data?.errorCode ?? 'somethingWentWrong'}`))
      }
    }
  }, [isError, setError, apiError, t])

  const handleSendEmailVerification = async (): Promise<void> => {
    try {
      if (!email) return
      await sendEmailVerification({ key: email }).unwrap()

      accountAnalytics.logResendEmail({ successful: true })
    } catch (exception) {
      accountAnalytics.logResendEmail({ successful: false, errorCode: exception?.data?.errorCode ?? exception?.error })
    }
  }

  const apiResponseMessage = isSuccess ? t('emailVerify:emailVerificationLinkSent') : error
  const iconType = isError || isUninitialized ? 'failure' : 'success'
  const buttonText = isUninitialized ? t('emailVerify:sendVerificationLink') : t('emailVerify:tryAgain')
  const iconMessage = isUninitialized ? t('emailVerify:notVerified') : apiResponseMessage

  return (
    <div className="mt-1 flex">
      {isLoading ? (
        <div className="text-center w-full mt-1">
          <Spinner className="ml-2 inline-block" color="purple" size={20} />
        </div>
      ) : (
        <>
          <IconWithMessage
            className="text-xxs items-center"
            hideIcon={isLoading}
            iconType={iconType}
            text={iconMessage}
          />
          {!isSuccess && (
            <ButtonClear
              className="ml-3 text-purple font-semibold tracking-wide"
              isAutoWidth
              noPadding
              onClick={handleSendEmailVerification}
              size="x-small"
            >
              {buttonText}
            </ButtonClear>
          )}
        </>
      )}
    </div>
  )
}
